/* eslint-disable complexity */
import React, { useState, useEffect } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    Link, Text, Box, Flex
} from 'components/ui';
import deliveryFrequency from 'utils/DeliveryFrequency';
import { globalModals, renderModal } from 'utils/globalModals';
import { globalModalsSelector } from 'selectors/page/headerFooterTemplate/data/globalModals/globalModalsSelector';
import basketUtils from 'utils/Basket';
import DateUtil from 'utils/Date';
import StringUtils from 'utils/String';
import LanguageLocaleUtils from 'utils/LanguageLocale';
import DeliveryOptionsEN from 'components/ProductPage/DeliveryOptions/locales/DeliveryOptions_en_US';
import DeliveryOptionsFR from 'components/ProductPage/DeliveryOptions/locales/DeliveryOptions_fr_CA';
import { isChangeQuantityEnabled } from 'utils/arChangeQuantityFlag';

const { AUTO_REPLENISH_PRODUCT_INFO } = globalModals;
const { formatSavingAmountString, formatFrequencyType, formatCurrency } = deliveryFrequency;
const getText = LanguageLocaleUtils.getLocalisedDataFromResource(DeliveryOptionsEN, DeliveryOptionsFR);

const PER_UNIT_QUANTITY = 1;

const buildTextResources = currentSku => {
    const { acceleratedPromotion } = currentSku || {};
    const { childOrderCount, promotionDuration, promotionExpirationDate, baseReplenishmentAdjuster } = acceleratedPromotion || {};

    const promoDiscount = formatSavingAmountString(currentSku, null, true);
    const basePromoDiscount = `${Math.ceil(baseReplenishmentAdjuster)}%`;
    const promoExpDate = DateUtil.getDateInMDYFormat(promotionExpirationDate);

    return {
        aboutAutoReplenish: getText('aboutAutoReplen'),
        deliveryEveryText: getText('deliveryEvery'),
        mostCommonText: getText('mostCommon'),
        autoReplenishLegalOptInText: getText('autoReplenishLegalOptIn'),
        subscriptionNotAvailableText: getText('subscriptionNotAvailable'),
        autoReplenishFirstTimeLegalDetails: getText('autoReplenishFirstTimeLegalDetails'),
        autoReplenishPromoLegalCopyShown: StringUtils.format(
            getText('autoReplenishFirstTimeLegalShown', ['{0}', '{1}', '{2}', '{3}']),
            promoDiscount,
            childOrderCount,
            promotionDuration,
            promoExpDate
        ),
        autoReplenishLegalMoreDetailsText: StringUtils.format(
            getText('autoReplenishFirstTimeLegalMoreDetails', ['{0}', '{1}', '{2}', '{3}']),
            childOrderCount,
            promoDiscount,
            promotionDuration,
            basePromoDiscount
        )
    };
};

const AutoReplenishment = ({
    currentSku,
    quantity,
    replenishmentFreqNum,
    replenishmentFreqType,
    isAutoReplenMostCommon,
    openDeliveryFrequencyModal,
    bccMediaSpecInfo,
    autoReplenishChecked,
    showBCCMediaModal
}) => {
    const reduxGlobalModals = useSelector(globalModalsSelector, shallowEqual);
    const [showAutoReplenishmentDetails, setShowAutoReplenishmentDetails] = useState(false);

    useEffect(() => {
        if (!autoReplenishChecked && showAutoReplenishmentDetails) {
            setShowAutoReplenishmentDetails(false);
        }
    }, [autoReplenishChecked]);

    const isReplenishmentEligible = currentSku?.isReplenishmentEligible;
    const listPrice = parseFloat(basketUtils.removeCurrency(currentSku?.listPrice));
    const textResources = buildTextResources(currentSku);

    const {
        aboutAutoReplenish,
        deliveryEveryText,
        mostCommonText,
        autoReplenishLegalOptInText,
        subscriptionNotAvailableText,
        autoReplenishFirstTimeLegalDetails,
        autoReplenishPromoLegalCopyShown,
        autoReplenishLegalMoreDetailsText
    } = textResources;

    const { acceleratedPromotion, replenishmentAdjuster } = currentSku || {};

    const displayQty =
        isChangeQuantityEnabled() && autoReplenishChecked ? Number(quantity) || PER_UNIT_QUANTITY : PER_UNIT_QUANTITY;

    return (
        <>
            <Flex
                flexWrap='wrap'
                marginBottom={1}
                alignItems='baseline'
            >
                {isReplenishmentEligible && replenishmentAdjuster && (
                    <Box
                        width={['100%', 'auto']}
                        marginTop={['.125em', 0]}
                    >
                        <Text
                            color='red'
                            fontWeight='bold'
                            children={`${formatSavingAmountString(currentSku, displayQty, false, false, true, acceleratedPromotion)} `}
                        />
                        <del children={`${formatCurrency(listPrice, displayQty)}`} />
                    </Box>
                )}
            </Flex>
            {isReplenishmentEligible ? (
                <>
                    <Link
                        onClick={() => openDeliveryFrequencyModal(true)}
                        arrowDirection='down'
                        padding={1}
                        margin={-1}
                    >
                        {deliveryEveryText}{' '}
                        <Text fontWeight='bold'>
                            {replenishmentFreqNum}&nbsp;
                            {formatFrequencyType(replenishmentFreqNum, replenishmentFreqType)}&nbsp;
                            {isAutoReplenMostCommon && `(${mostCommonText})`}
                        </Text>
                    </Link>
                    {acceleratedPromotion && (
                        <Text
                            is='p'
                            marginTop={3}
                            fontSize='sm'
                            color='gray'
                        >
                            {`${autoReplenishPromoLegalCopyShown} `}
                            {!showAutoReplenishmentDetails && (
                                <Link
                                    onClick={() => setShowAutoReplenishmentDetails(true)}
                                    fontSize='sm'
                                    color='blue'
                                    children={autoReplenishFirstTimeLegalDetails}
                                />
                            )}
                        </Text>
                    )}
                    {showAutoReplenishmentDetails && (
                        <Text
                            is='p'
                            marginTop={3}
                            fontSize='sm'
                            color='gray'
                            children={autoReplenishLegalMoreDetailsText}
                        />
                    )}
                    <Text
                        is='p'
                        marginTop={3}
                        fontSize='sm'
                        color='gray'
                        children={autoReplenishLegalOptInText}
                    />
                </>
            ) : (
                <Text
                    is='p'
                    color='gray'
                    children={subscriptionNotAvailableText}
                />
            )}
            {aboutAutoReplenish && (
                <Text
                    is='p'
                    marginTop={2}
                >
                    <Link
                        display='block'
                        color='blue'
                        onClick={e =>
                            renderModal(reduxGlobalModals[AUTO_REPLENISH_PRODUCT_INFO], () =>
                                showBCCMediaModal(e, { type: 'information', info: bccMediaSpecInfo })
                            )
                        }
                        padding={2}
                        margin={-2}
                        children={aboutAutoReplenish}
                    />
                </Text>
            )}
        </>
    );
};

export default wrapFunctionalComponent(AutoReplenishment, 'AutoReplenishment');
